import { useState } from 'react'
import { fetchData } from '../../../../global/utils/fetch'
import { STATUS } from '../../../../global/types/audit'
import { IAudit } from '../../../../../../app/entities/Audit'
import { getFailedExamples as getFailedExamplesAPI, lookupAudit as lookupAuditAPI } from './api'

interface UseModalStateOptions {
  auditType: STATUS | null
  sectionKey: string | null
}

export function useModalState({ auditType, sectionKey }: UseModalStateOptions) {
  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState<string | null>(null)

  // Failed examples modal state
  const [showFailedExamples, setShowFailedExamples] = useState(false)
  const [failedExamples, setFailedExamples] = useState<any[]>([])
  const [failedExamplesPage, setFailedExamplesPage] = useState(0)
  const [isLoadingFailedExamples, setIsLoadingFailedExamples] = useState(false)
  const [failedExamplesExhausted, setFailedExamplesExhausted] = useState(false)
  const [failedExamplesSearchInfo, setFailedExamplesSearchInfo] = useState<
    string | null
  >(null)

  // Lookup audit modal state
  const [lookupAuditId, setLookupAuditId] = useState('')
  const [lookedUpAudit, setLookedUpAudit] = useState<any | null>(null)
  const [isLoadingLookup, setIsLoadingLookup] = useState(false)

  // Fetch failed examples
  const handleFetchFailedExamples = async (loadMore: boolean = false) => {
    if (!auditType || !sectionKey) return

    setIsLoadingFailedExamples(true)
    try {
      const page = loadMore ? failedExamplesPage + 1 : 0
      const response = await fetchData<{
        examples: any[]
        actualPage: number
        exhausted: boolean
        searchedPages: number
      }>(getFailedExamplesAPI(auditType, sectionKey, page, 5))

      // Build info message about the search
      let searchInfo: string | null = null
      if (response.searchedPages > 1) {
        searchInfo = `Searched ${response.searchedPages} page(s) to find examples with images`
      }
      if (response.exhausted) {
        searchInfo = searchInfo
          ? `${searchInfo}. No more failed audits available.`
          : 'No more failed audits available with required images.'
      }

      if (loadMore) {
        setFailedExamples((prev) => [...prev, ...response.examples])
        setFailedExamplesPage(response.actualPage)
        setFailedExamplesExhausted(response.exhausted)
        setFailedExamplesSearchInfo(searchInfo)
      } else {
        setFailedExamples(response.examples)
        setFailedExamplesPage(response.actualPage)
        setFailedExamplesExhausted(response.exhausted)
        setFailedExamplesSearchInfo(searchInfo)
        setShowFailedExamples(true)
      }

      // If no examples found and exhausted, show message
      if (response.examples.length === 0 && response.exhausted) {
        alert(
          'No failed audits found with the required images for this section.',
        )
      }
    } catch (error) {
      console.error('Failed to fetch failed examples:', error)
      alert('Failed to load failed examples')
    } finally {
      setIsLoadingFailedExamples(false)
    }
  }

  // Lookup specific audit by objectId
  const handleLookupAudit = async () => {
    if (!lookupAuditId.trim()) {
      alert('Please enter an Audit ID')
      return
    }

    setIsLoadingLookup(true)
    try {
      const response = await fetchData<IAudit>(lookupAuditAPI(lookupAuditId.trim()))
      setLookedUpAudit(response)
    } catch (error) {
      console.error('Failed to lookup audit:', error)
      alert('Failed to find audit. Please check the ID and try again.')
      setLookedUpAudit(null)
    } finally {
      setIsLoadingLookup(false)
    }
  }

  return {
    // Lightbox
    lightboxImage,
    setLightboxImage,

    // Failed examples
    showFailedExamples,
    setShowFailedExamples,
    failedExamples,
    failedExamplesPage,
    isLoadingFailedExamples,
    failedExamplesExhausted,
    failedExamplesSearchInfo,
    handleFetchFailedExamples,

    // Lookup audit
    lookupAuditId,
    setLookupAuditId,
    lookedUpAudit,
    setLookedUpAudit,
    isLoadingLookup,
    handleLookupAudit,
  }
}
